/**
 * A class that provides a unique identifier akin to object identity.
 *
 * Meant to be used as a superclass to object that needs this functionality.
 */
class Unique {
  /**
   * Locally unique identifier tied to object identity. Can be used as
   * React keys in lists. `id` is ignored when formulas are compared for
   * syntactical equality.
   *
   * Local here means that uniqueness is limited to the program instance.
   * Code running on different machines (or different tabs) will have
   * clashing identifiers. Additionally, saving `id` and later restoring
   * it is not safe.
   */
  readonly id: string;

  /**
   * Used to generate `id`s.
   */
  private static nextId: number = 0;

  constructor(u: Unique | null = null) {
    if (u === null) {
      // Reset id generator if we are out of unique identifiers. Hopefully this
      // never happens, or old objects are unreachable by this point.
      if (Unique.nextId === Number.MAX_SAFE_INTEGER) {
        Unique.nextId = 0;
      }
      this.id = Unique.nextId.toString();
      Unique.nextId += 1;
    } else {
      this.id = u.id;
    }
  }
}

export default Unique;
